import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, input, OnChanges, OnInit, signal } from '@angular/core';
import { getFileTypeIconClass } from '@iupics-components/standard/preview-doc/preview-doc.component';
import { DocumentFilesService } from './document-files.service';

/** Thumbnail of a tile: drawn when the tile comes into view (images, first page of PDFs), else the file icon */
@Component({
  selector: 'iu-document-thumb',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (url(); as u) {
      <img class="dt-img" [src]="u" alt="" draggable="false" />
    } @else {
      <i class="dt-icon" [class]="icon()" aria-hidden="true"></i>
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 110px;
      overflow: hidden;
    }
    .dt-img {
      max-width: 100%;
      max-height: 110px;
      object-fit: contain;
      border-radius: 3px;
      box-shadow: 0 1px 3px #0003;
      background: #fff;
    }
    .dt-icon {
      font-size: 40px;
      color: var(--docs-ft-color-muted);
    }
  `,
})
export class DocumentThumbComponent implements OnInit, OnChanges {
  readonly docId = input.required<string>();
  readonly fileName = input('');
  readonly size = input<number | null>(null);
  readonly version = input(1);

  readonly #files = inject(DocumentFilesService);
  readonly #host = inject(ElementRef<HTMLElement>);
  readonly #cd = inject(ChangeDetectorRef);

  readonly url = signal<string | null>(null);
  readonly icon = signal('icon-file');
  #visible = false;
  #observer: IntersectionObserver | null = null;
  #seq = 0;
  #destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.#destroyed = true;
      this.#observer?.disconnect();
    });
  }

  ngOnInit(): void {
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.#visible = true;
          this.#observer?.disconnect();
          this.#load();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#observer.observe(this.#host.nativeElement as HTMLElement);
  }

  ngOnChanges(): void {
    this.icon.set(getFileTypeIconClass(this.fileName()));
    this.url.set(null);
    if (this.#visible) this.#load();
  }

  #load(): void {
    const seq = ++this.#seq;
    if (!this.#files.hasThumb(this.fileName(), this.size())) return;
    this.#files.thumb(this.docId(), this.fileName(), this.size(), `${this.docId()}#${this.version()}`).then((url) => {
      if (seq !== this.#seq || this.#destroyed) return;
      this.url.set(url);
      // the window may not be refreshed by the application tick: check this view
      this.#cd.markForCheck();
      this.#cd.detectChanges();
    });
  }
}
